import { round2 } from "@/lib/format";

const COLS = ["products", "customers", "orders", "suppliers", "sales", "payments", "purchases", "supplier_payments", "promotions", "campaigns", "conflicts", "returns", "expenses", "shifts", "hr_events", "attendance", "patients", "appointments", "encounters", "services", "clinic_records", "vault_moves", "invoices", "house_periods", "day_closes", "fx_trades", "fx_transfers", "bank_moves", "custom_orders", "charity_moves", "waste"];
const CLINIC_COLS = { "patient.upsert": "patients", "appointment.upsert": "appointments", "encounter.upsert": "encounters", "service.upsert": "services", "record.upsert": "clinic_records" };

export const emptyBase = () => ({
  shop: null, staff: [], activity: [], lastSync: null,
  ...Object.fromEntries(COLS.map((c) => [c, {}])),
});

export function mergePull(base, data) {
  const next = { ...base, shop: data.shop, staff: data.staff, activity: data.activity, lastSync: data.server_time };
  COLS.forEach((c) => {
    const col = data.full ? {} : { ...base[c] };
    (data[c] || []).forEach((doc) => { col[doc.id] = doc; });
    next[c] = col;
  });
  return next;
}

// Mirrors server-side effects so the device shows correct state before sync.
export function applyLocal(s, op) {
  const d = op.data;
  const n = { ...s };
  const get = (col, id) => n[col]?.[id];
  const put = (col, doc) => { n[col] = { ...n[col], [doc.id]: doc }; };
  const stock = (pid, delta) => {
    const p = get("products", pid);
    if (p) put("products", { ...p, stock: round2((p.stock || 0) + delta) });
  };
  const meta = { staff_id: op.staff_id, staff_name: op.staff_name, created_at: op.ts, pending: true };
  switch (op.type) {
    case "product.upsert": {
      const { base_price, ...rest } = d;
      const p = get("products", d.id);
      put("products", p ? { ...p, ...rest, stock: p.stock } : { stock: 0, deleted: false, ...rest, created_at: op.ts });
      break;
    }
    case "product.delete":
      if (get("products", d.id)) put("products", { ...get("products", d.id), deleted: true });
      break;
    case "stock.adjust":
      stock(d.product_id, Number(d.delta));
      break;
    case "waste.create": {
      if (get("waste", d.id)) break;
      const p = d.product_id && get("products", d.product_id);
      const cost = Number(d.cost ?? p?.cost ?? 0);
      if (p) stock(p.id, -Number(d.qty));
      put("waste", { ...d, name: d.name || p?.name || "", qty: Number(d.qty), cost, loss: round2(Number(d.qty) * cost), ...meta });
      break;
    }
    case "waste.delete": {
      const w = get("waste", d.id);
      if (w && !w.deleted) { if (w.product_id) stock(w.product_id, Number(w.qty)); put("waste", { ...w, deleted: true }); }
      break;
    }
    case "price.bulk":
      d.changes.forEach((c) => { const p = get("products", c.id); if (p) put("products", { ...p, price: c.price }); });
      break;
    case "sale.create": {
      if (get("sales", d.id)) break;
      d.items.forEach((i) => i.product_id && stock(i.product_id, -Number(i.qty)));
      const credit = ["credit", "split", "advance"].includes(d.payment) ? round2(d.total - (d.paid || 0)) : 0;
      const points = Math.floor(d.total / (n.shop?.loyalty_per || 100));
      const c = d.customer_id && get("customers", d.customer_id);
      if (c) put("customers", { ...c, balance: round2((c.balance || 0) + credit), points: (c.points || 0) + points, last_purchase_at: op.ts });
      put("sales", { ...d, credit, points, ...meta });
      if (d.charity > 0) put("charity_moves", { id: `${d.id}-ch`, kind: "in", amount: d.charity, sale_id: d.id, receipt_no: d.receipt_no, method: d.payment, staff_name: op.staff_name, customer_name: d.customer_name, name: d.charity_name || "", created_at: op.ts, pending: true });
      break;
    }
    case "customer.upsert": {
      const c = get("customers", d.id);
      put("customers", c ? { ...c, ...d } : { balance: 0, points: 0, last_purchase_at: null, ...d, created_at: op.ts });
      break;
    }
    case "payment.create": {
      if (get("payments", d.id)) break;
      const c = get("customers", d.customer_id);
      if (c) put("customers", { ...c, balance: round2((c.balance || 0) + (d.kind === "refund" ? 1 : -1) * Number(d.amount)) });
      put("payments", { ...d, ...meta });
      break;
    }
    case "supplier.upsert": {
      const sp = get("suppliers", d.id);
      put("suppliers", { balance: 0, ...sp, ...d });
      break;
    }
    case "purchase.create": {
      if (get("purchases", d.id)) break;
      d.items.forEach((i) => {
        stock(i.product_id, Number(i.qty));
        const p = get("products", i.product_id);
        if (p) put("products", { ...p, cost: Number(i.cost) });
      });
      const sp = get("suppliers", d.supplier_id);
      if (sp) put("suppliers", { ...sp, balance: round2((sp.balance || 0) + d.total - (d.paid ?? d.total)) });
      put("purchases", { ...d, ...meta });
      break;
    }
    case "supplier_payment.create": {
      if (get("supplier_payments", d.id)) break;
      const sp = get("suppliers", d.supplier_id);
      if (sp) put("suppliers", { ...sp, balance: round2((sp.balance || 0) - Number(d.amount)) });
      put("supplier_payments", { ...d, ...meta });
      break;
    }
    case "sale.return": {
      if (get("returns", d.id)) break;
      const sale = get("sales", d.sale_id);
      d.items.forEach((i) => i.product_id && stock(i.product_id, Number(i.qty)));
      if (sale) {
        put("sales", { ...sale, returned: round2((sale.returned || 0) + Number(d.amount)) });
        const c = d.refund === "credit" && sale.customer_id && get("customers", sale.customer_id);
        if (c) put("customers", { ...c, balance: round2((c.balance || 0) + (d.kind === "refund" ? 1 : -1) * Number(d.amount)) });
      }
      put("returns", { ...d, ...meta });
      break;
    }
    case "expense.create":
      if (!get("expenses", d.id)) put("expenses", { ...d, amount: Number(d.amount), ...meta });
      break;
    case "order.upsert": {
      const prev = get("orders", d.id);
      const total = round2((d.items || []).reduce((s, i) => s + Number(i.cost ?? i.price ?? 0) * Number(i.qty), 0));
      put("orders", { ...(prev || { created_at: op.ts, created_by: op.staff_name, number: Object.keys(n.orders || {}).length + 1 }), ...d, total, deposit: Number(d.deposit) || 0, updated_at: op.ts, pending: true });
      break;
    }
    case "charity.payout":
      put("charity_moves", { id: d.id, kind: "out", amount: Number(d.amount) || 0, note: d.note || "", recipient: d.recipient || "", staff_name: op.staff_name, created_at: op.ts, pending: true });
      break;
    case "custom_order.upsert": {
      const prev = get("custom_orders", d.id);
      put("custom_orders", { ...(prev || { created_at: op.ts, created_by: op.staff_name, number: Object.keys(n.custom_orders || {}).length + 1 }), ...d, price: Number(d.price) || 0, deposit: Number(d.deposit) || 0, updated_at: op.ts, pending: true });
      break;
    }
    case "shift.open":
      if (!get("shifts", d.id)) put("shifts", { ...d, status: "open", opened_at: op.ts, opened_by: op.staff_name, moves: [], pending: true });
      break;
    case "cash.move": {
      const sh = get("shifts", d.shift_id);
      if (sh && !(sh.moves || []).some((m) => m.id === d.id)) put("shifts", { ...sh, moves: [...(sh.moves || []), { ...d, amount: Number(d.amount) || 0, ts: op.ts, staff_name: op.staff_name }] });
      break;
    }
    case "bank.move": {
      if (get("bank_moves", d.id)) break;
      put("bank_moves", { ...d, amount: Number(d.amount) || 0, created_at: op.ts, staff_name: op.staff_name });
      if (d.source === "from_vault" || d.source === "to_vault") put("vault_moves", { id: `${d.id}-v`, kind: d.source === "from_vault" ? "out" : "in", amount: Number(d.amount) || 0, source: d.source === "from_vault" ? "to_bank" : "from_bank", bank_move_id: d.id, note: d.note || "", ticket_no: d.ticket_no || "", created_at: op.ts, staff_name: op.staff_name });
      break;
    }
    case "bank.void": {
      const m = get("bank_moves", d.id);
      if (m) put("bank_moves", { ...m, voided: true, void_reason: d.reason || "" });
      const v = get("vault_moves", `${d.id}-v`);
      if (v) put("vault_moves", { ...v, voided: true });
      break;
    }
    case "vault.move": {
      if (!get("vault_moves", d.id)) put("vault_moves", { ...d, amount: Number(d.amount) || 0, created_at: op.ts, staff_name: op.staff_name });
      break;
    }
    case "shift.close": {
      const sh = get("shifts", d.id);
      if (sh) put("shifts", { ...sh, ...d, status: "closed", closed_at: op.ts, closed_by: op.staff_name });
      break;
    }
    case "shift.reopen": {
      const sh = get("shifts", d.id);
      if (sh) { const { closed_at, closed_by, counted, expected, report, ...rest } = sh; put("shifts", { ...rest, status: "open", reopened_at: op.ts }); }
      Object.values(n.vault_moves || {}).filter((m) => m.source === "shift_close" && m.shift_id === d.id).forEach((m) => put("vault_moves", { ...m, voided: true }));
      break;
    }
    case "invoice.create": {
      if (get("invoices", d.id)) break;
      put("invoices", { ...d, number: d.number || "…", status: "issued", ...meta });
      (d.sale_ids || []).forEach((sid) => { const s = get("sales", sid); if (s) put("sales", { ...s, invoice_id: d.id, invoice_no: d.number || "…" }); });
      break;
    }
    case "invoice.void": {
      const inv = get("invoices", d.id);
      if (inv) { put("invoices", { ...inv, status: "void", void_reason: d.reason }); (inv.sale_ids || []).forEach((sid) => { const s = get("sales", sid); if (s) { const { invoice_id, invoice_no, ...r } = s; put("sales", r); } }); }
      break;
    }
    case "house.open":
      if (!get("house_periods", d.id)) put("house_periods", { ...d, status: "open", opened_at: op.ts, opened_by: op.staff_name, pending: true });
      break;
    case "house.close": {
      const hp = get("house_periods", d.id);
      if (hp) put("house_periods", { ...hp, status: "closed", closed_at: op.ts, closed_by: op.staff_name, close_note: d.note });
      break;
    }
    case "day.close":
      put("day_closes", { ...get("day_closes", d.id), ...d, status: "closed", closed_at: op.ts, closed_by: op.staff_name });
      break;
    case "fx.trade":
    case "fx.adjust":
      if (!get("fx_trades", d.id)) {
        put("fx_trades", { method: "cash", ...d, type: op.type === "fx.trade" ? "trade" : "adjust", total: op.type === "fx.trade" ? round2(Number(d.amount) * Number(d.rate)) : 0, receipt_no: d.id.slice(0, 6).toUpperCase(), status: op.type === "fx.trade" && d.method === "transfer" && d.kind === "buy" ? "pending" : undefined, ...meta });
        const tf = d.transfer_id && get("fx_transfers", d.transfer_id);
        if (tf && !tf.applied_to) put("fx_transfers", { ...tf, applied_to: d.id, applied_at: op.ts });
      }
      break;
    case "fx.transfer.report":
      if (!get("fx_transfers", d.id)) put("fx_transfers", { status: "reported", applied_to: null, ...d, ...meta });
      break;
    case "fx.transfer.reconcile": {
      const tf = get("fx_transfers", d.id);
      if (tf) put("fx_transfers", { ...tf, status: d.value === false ? "reported" : "reconciled", reconciled_at: op.ts, reconciled_by: op.staff_name });
      break;
    }
    case "fx.transfer.delete": {
      const tf = get("fx_transfers", d.id);
      if (tf && !tf.applied_to) put("fx_transfers", { ...tf, deleted: true });
      break;
    }
    case "fx.payout": {
      const tr = get("fx_trades", d.id);
      if (tr && tr.status === "pending" && String(d.pin) === String(tr.pin)) put("fx_trades", { ...tr, status: "paid", paid_at: op.ts, paid_by: op.staff_name, receiver_doc: d.receiver_doc || "" });
      break;
    }
    case "fx.void": {
      const tr = get("fx_trades", d.id);
      if (tr) put("fx_trades", { ...tr, voided: true, void_reason: d.reason });
      break;
    }
    case "day.reopen": {
      const dc = get("day_closes", d.id);
      if (dc) put("day_closes", { ...dc, status: "reopened", reopened_at: op.ts, reopened_by: op.staff_name });
      break;
    }
    case "promotion.upsert":
      put("promotions", { ...get("promotions", d.id), ...d });
      break;
    case "shop.update":
      n.shop = { ...n.shop, ...d };
      break;
    case "hr.event":
      put("hr_events", { ...get("hr_events", d.id), ...d, amount: Number(d.amount) || 0, days: Number(d.days) || 0, by: op.staff_name, created_at: get("hr_events", d.id)?.created_at || op.ts });
      break;
    case "service.delete": {
      const s = get("services", d.id);
      if (s) put("services", { ...s, deleted: true });
      break;
    }
    case "attendance.in":
      if (!get("attendance", d.id)) put("attendance", { id: d.id, staff_id: d.staff_id || op.staff_id, staff_name: d.staff_name || op.staff_name, in_at: op.ts, out_at: null, pending: true });
      break;
    case "attendance.out": {
      const a = get("attendance", d.id);
      if (a && !a.out_at) put("attendance", { ...a, out_at: op.ts });
      break;
    }
    case "expense.delete": {
      const ex = get("expenses", d.id);
      if (ex) put("expenses", { ...ex, deleted: true });
      break;
    }
    case "hr.event.delete": {
      const ev = get("hr_events", d.id);
      if (ev) put("hr_events", { ...ev, deleted: true });
      // Removing a paid payroll (or its salary payment) also reverses the linked salary expense
      if (ev?.kind === "payroll" && ev.status === "paid") { const ex = get("expenses", `exp-${ev.id}`); if (ex) put("expenses", { ...ex, deleted: true }); }
      break;
    }
    case "favorites.update":
      n.shop = { ...n.shop, favorites: d.pages };
      break;
    default:
      if (CLINIC_COLS[op.type]) {
        const col = CLINIC_COLS[op.type];
        const prev = get(col, d.id);
        put(col, { ...(prev || { created_at: op.ts, created_by: op.staff_name, deleted: false }), ...d, pending: true });
      }
      break;
  }
  return n;
}
